import { useState, useEffect } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface Integration {
  id: string
  name: string
  icon: string
  description: string
  status: 'connected' | 'disconnected' | 'available'
  category: 'deploy' | 'auth'
  action?: () => void
  actionLabel?: string
  details?: string
}

export function IntegrationsPanel() {
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [loading, setLoading] = useState(true)
  const [testResult, setTestResult] = useState<{ id: string; ok: boolean; msg: string } | null>(null)

  useEffect(() => { checkIntegrations() }, [])

  async function checkIntegrations() {
    setLoading(true)
    try {
      const ghRes = await fetch(apiUrl('/api/auth/github'), { method: 'HEAD', redirect: 'manual' }).catch(() => null)
      const ghConfigured = ghRes?.status === 302 || ghRes?.status === 307

      const cfRes = await authFetch(apiUrl('/api/deploy/projects')).catch(() => null)
      const cfOk = cfRes?.ok ?? false

      setIntegrations([
        {
          id: 'cloudflare',
          name: 'Cloudflare Pages',
          icon: '☁️',
          description: 'Deploy your apps to the edge. Fast, free, automatic SSL.',
          status: cfOk ? 'connected' : 'disconnected',
          category: 'deploy',
          details: cfOk ? 'Ready — click Deploy in any project to publish' : 'Deploy service unavailable',
          actionLabel: 'Test',
          action: () => testIntegration('cloudflare'),
        },
        {
          id: 'vercel',
          name: 'Vercel',
          icon: '▲',
          description: 'Deploy to your own Vercel account.',
          status: 'available',
          category: 'deploy',
          details: 'Add your VERCEL_TOKEN in Settings > Secrets to connect',
          actionLabel: 'How to Connect',
          action: () => setTestResult({ id: 'vercel', ok: true, msg: '1. Go to vercel.com/account/tokens\n2. Create a token\n3. In PULSE, go to Settings > Secrets\n4. Add: VERCEL_TOKEN = your_token\n5. Deploy panel will show Vercel option' }),
        },
        {
          id: 'netlify',
          name: 'Netlify',
          icon: '◆',
          description: 'Deploy to your own Netlify account.',
          status: 'available',
          category: 'deploy',
          details: 'Add your NETLIFY_AUTH_TOKEN in Settings > Secrets to connect',
          actionLabel: 'How to Connect',
          action: () => setTestResult({ id: 'netlify', ok: true, msg: '1. Go to app.netlify.com/user/applications\n2. Create a personal access token\n3. In PULSE, go to Settings > Secrets\n4. Add: NETLIFY_AUTH_TOKEN = your_token\n5. Deploy panel will show Netlify option' }),
        },
        {
          id: 'github',
          name: 'GitHub',
          icon: '🐙',
          description: 'Sign in with GitHub & auto-deploy on push.',
          status: ghConfigured ? 'connected' : 'disconnected',
          category: 'auth',
          details: ghConfigured ? 'Connected — sign in with GitHub available' : 'GitHub OAuth not configured',
          actionLabel: 'Test',
          action: () => testIntegration('github'),
        },
        {
          id: 'google',
          name: 'Google',
          icon: '🔵',
          description: 'Sign in with Google.',
          status: 'connected',
          category: 'auth',
          details: 'Connected — sign in with Google available',
          actionLabel: 'Test',
          action: () => testIntegration('google'),
        },
      ])
    } catch (e) {
      console.error('Failed to check integrations:', e)
    } finally {
      setLoading(false)
    }
  }

  async function testIntegration(id: string) {
    setTestResult(null)
    try {
      switch (id) {
        case 'cloudflare': {
          const res = await authFetch(apiUrl('/api/deploy/projects'))
          if (res.ok) {
            const projects = await res.json()
            setTestResult({ id, ok: true, msg: `✅ Connected! ${projects.length} deployable project${projects.length !== 1 ? 's' : ''} found in your workspace.` })
          } else {
            setTestResult({ id, ok: false, msg: '❌ Could not reach deploy service' })
          }
          break
        }
        case 'github': {
          window.open(apiUrl('/api/auth/github'), '_blank', 'width=500,height=600')
          setTestResult({ id, ok: true, msg: '✅ GitHub OAuth window opened — complete the login to verify.' })
          break
        }
        case 'google': {
          window.open(apiUrl('/api/auth/google'), '_blank', 'width=500,height=600')
          setTestResult({ id, ok: true, msg: '✅ Google OAuth window opened — complete the login to verify.' })
          break
        }
      }
    } catch (e: any) {
      setTestResult({ id, ok: false, msg: `❌ Error: ${e.message}` })
    }
  }

  const categories = [
    { key: 'deploy', label: 'Deployment', icon: '🚀' },
    { key: 'auth', label: 'Authentication', icon: '🔐' },
  ]

  if (loading) {
    return (
      <div style={{ padding: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
        <div style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.1)', borderTopColor: '#7c6ef5', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontFamily: 'ui-monospace, monospace' }}>Checking integrations...</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    )
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 800, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#fff' }}>Integrations</h2>
        <p style={{ margin: '6px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.5)', fontFamily: 'ui-monospace, monospace' }}>
          Connect external services to your workspace
        </p>
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ padding: '8px 14px', borderRadius: 8, background: 'rgba(95,212,164,0.1)', border: '1px solid rgba(95,212,164,0.3)', fontSize: 11, fontFamily: 'ui-monospace, monospace', color: '#5fd4a4' }}>
          🟢 {integrations.filter(i => i.status === 'connected').length} Connected
        </div>
        <div style={{ padding: '8px 14px', borderRadius: 8, background: 'rgba(124,110,245,0.1)', border: '1px solid rgba(124,110,245,0.3)', fontSize: 11, fontFamily: 'ui-monospace, monospace', color: '#7c6ef5' }}>
          🔗 {integrations.filter(i => i.status === 'available').length} Available
        </div>
        {integrations.filter(i => i.status === 'disconnected').length > 0 && (
          <div style={{ padding: '8px 14px', borderRadius: 8, background: 'rgba(240,112,112,0.1)', border: '1px solid rgba(240,112,112,0.3)', fontSize: 11, fontFamily: 'ui-monospace, monospace', color: '#f07070' }}>
            ⚠️ {integrations.filter(i => i.status === 'disconnected').length} Disconnected
          </div>
        )}
      </div>

      {/* Test result */}
      {testResult && (
        <div style={{
          padding: '12px 16px', borderRadius: 8, marginBottom: 16,
          background: testResult.ok ? 'rgba(95,212,164,0.08)' : 'rgba(240,112,112,0.08)',
          border: `1px solid ${testResult.ok ? 'rgba(95,212,164,0.3)' : 'rgba(240,112,112,0.3)'}`,
          fontSize: 12, fontFamily: 'ui-monospace, monospace',
          color: testResult.ok ? '#5fd4a4' : '#f07070',
          whiteSpace: 'pre-wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ flex: 1 }}>{testResult.msg}</span>
            <button onClick={() => setTestResult(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 14, flexShrink: 0 }}>✕</button>
          </div>
        </div>
      )}

      {/* Categories */}
      {categories.map(cat => {
        const items = integrations.filter(i => i.category === cat.key)
        if (items.length === 0) return null
        return (
          <div key={cat.key} style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10, fontFamily: 'ui-monospace, monospace' }}>
              {cat.icon} {cat.label}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {items.map(int => (
                <div key={int.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                  borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)',
                  background: 'rgba(255,255,255,0.02)',
                }}>
                  <span style={{ fontSize: 24, width: 36, textAlign: 'center' }}>{int.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{int.name}</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{int.description}</div>
                    {int.details && (
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 4, fontFamily: 'ui-monospace, monospace' }}>{int.details}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span style={{
                      fontSize: 9, padding: '3px 8px', borderRadius: 6, fontWeight: 700, fontFamily: 'ui-monospace, monospace',
                      background: int.status === 'connected' ? 'rgba(95,212,164,0.12)' : int.status === 'available' ? 'rgba(124,110,245,0.12)' : 'rgba(240,112,112,0.12)',
                      color: int.status === 'connected' ? '#5fd4a4' : int.status === 'available' ? '#7c6ef5' : '#f07070',
                    }}>
                      {int.status === 'connected' ? '● Connected' : int.status === 'available' ? '○ Available' : '○ Disconnected'}
                    </span>
                    {int.action && (
                      <button onClick={int.action} style={{
                        padding: '5px 12px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                        border: '1px solid rgba(124,110,245,0.4)', background: 'rgba(124,110,245,0.08)',
                        color: '#7c6ef5', cursor: 'pointer', fontFamily: 'ui-monospace, monospace',
                      }}>
                        {int.actionLabel}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      })}

      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <button onClick={checkIntegrations} style={{
          padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
          background: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer',
          fontSize: 11, fontFamily: 'ui-monospace, monospace',
        }}>
          ↻ Refresh Status
        </button>
      </div>
    </div>
  )
}
